import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { emailsAPI, contactsAPI, agentsAPI } from '../../services/api';
import { Paperclip, FileText } from 'lucide-react';
import { EmailThread, CompanyUser, Agent, AttachmentRef } from '../../types';
import { useToast } from '../../contexts/ToastContext';
import { Send, X, Info, Sparkles, Download, Square, CheckSquare, Bot, ExternalLink } from 'lucide-react';
import RichTextEditor from '../RichTextEditor';
import AttachmentPicker from './AttachmentPicker';
import { formatServerDate, formatServerDateTime } from '../../utils/datetime';

interface EmailsTabProps {
    agents: Agent[];
    teamMembers: CompanyUser[];
    hasAssignPermission: boolean;
    mobilePanel: 'list' | 'chat' | 'details';
    selectedEmail: EmailThread | null;
    setSelectedEmail: React.Dispatch<React.SetStateAction<EmailThread | null>>;
    setMobilePanel: (panel: 'list' | 'chat' | 'details') => void;
    showDetails: boolean;
    setShowDetails: (show: boolean) => void;
    initialComposeEmail?: string;
    /** Agent to preselect in the composer (a ticket knows which mailbox). */
    initialComposeAgentId?: string;
    onComposeHandled?: () => void;
    // Deep-link: auto-select a specific email thread by ID
    initialEmailId?: string;
    onInitialEmailHandled?: () => void;
}

const EmailsTab: React.FC<EmailsTabProps> = ({
    agents,
    teamMembers,
    hasAssignPermission,
    mobilePanel,
    selectedEmail,
    setSelectedEmail,
    setMobilePanel,
    showDetails,
    setShowDetails,
    initialComposeEmail,
    initialComposeAgentId,
    onComposeHandled,
    initialEmailId,
    onInitialEmailHandled,
}) => {
    const { showToast } = useToast();

    const [emails, setEmails] = useState<EmailThread[]>([]);
    const [loadingEmails, setLoadingEmails] = useState(false);
    const [selectedAgentId, setSelectedAgentId] = useState<string>('');
    const [selectedEmailIds, setSelectedEmailIds] = useState<Set<string>>(new Set());

    const [replyText, setReplyText] = useState('');
    const [replyHtml, setReplyHtml] = useState('');
    const [sendingReply, setSendingReply] = useState(false);
    // Real file attachments for the reply (inline images stay in RichTextEditor).
    const [replyAttachments, setReplyAttachments] = useState<AttachmentRef[]>([]);
    const [replyUploading, setReplyUploading] = useState(false);
    const [generatingReply, setGeneratingReply] = useState(false);

    const [commentText, setCommentText] = useState('');
    const [addingComment, setAddingComment] = useState(false);
    const [assigning, setAssigning] = useState(false);

    const [showComposeModal, setShowComposeModal] = useState(false);
    // Agent id asked for by a deep link, held until `agents` contains it.
    const [pendingComposeAgentId, setPendingComposeAgentId] = useState('');
    const [composeData, setComposeData] = useState({
        agentId: '',
        toAddresses: [] as string[],
        subject: '',
        textBody: '',
        htmlBody: ''
    });
    const [sendingNew, setSendingNew] = useState(false);
    const [composeAttachments, setComposeAttachments] = useState<AttachmentRef[]>([]);
    const [composeUploading, setComposeUploading] = useState(false);

    // Auto-reply Toggle State
    const currentAgent = selectedEmail ? agents.find(a => a.id === selectedEmail.agent_id) : null;
    const composeAgent = composeData.agentId ? agents.find(a => a.id === composeData.agentId) : null;
    const activeAgentForToggle = showComposeModal ? composeAgent : currentAgent;
    const [autoReplyEnabled, setAutoReplyEnabled] = useState(false);
    const [updatingAutoReply, setUpdatingAutoReply] = useState(false);

    useEffect(() => {
        if (activeAgentForToggle) {
            setAutoReplyEnabled(!!activeAgentForToggle.config.auto_respond_emails);
        }
    }, [activeAgentForToggle, showComposeModal]);

    const handleToggleAutoReply = async () => {
        if (!activeAgentForToggle) return;
        setUpdatingAutoReply(true);
        const newValue = !autoReplyEnabled;
        setAutoReplyEnabled(newValue);

        try {
            await agentsAPI.update(activeAgentForToggle.id, {
                config: {
                    ...activeAgentForToggle.config,
                    auto_respond_emails: newValue
                }
            });
            showToast(`Auto-Reply ${newValue ? 'enabled' : 'disabled'} for ${activeAgentForToggle.config.agent_name}`, 'success');
        } catch (error) {
            console.error('Failed to toggle auto-reply', error);
            setAutoReplyEnabled(!newValue);
            showToast('Failed to update auto-reply settings', 'error');
        } finally {
            setUpdatingAutoReply(false);
        }
    };

    // Published Toggle State
    const [publishedEnabled, setPublishedEnabled] = useState(false);
    const [updatingPublished, setUpdatingPublished] = useState(false);

    useEffect(() => {
        if (activeAgentForToggle) {
            setPublishedEnabled(!!activeAgentForToggle.is_published);
        }
    }, [activeAgentForToggle, showComposeModal]);

    const handleTogglePublished = async () => {
        if (!activeAgentForToggle) return;
        setUpdatingPublished(true);
        const newValue = !publishedEnabled;
        setPublishedEnabled(newValue);

        try {
            await agentsAPI.update(activeAgentForToggle.id, {
                is_published: newValue
            });
            showToast(`Agent ${activeAgentForToggle.config.agent_name} is now ${newValue ? 'Live' : 'Draft'}`, 'success');
        } catch (error) {
            console.error('Failed to toggle published status', error);
            setPublishedEnabled(!newValue);
            showToast('Failed to update published status', 'error');
        } finally {
            setUpdatingPublished(false);
        }
    };

    // Multi-select Contact State
    const [contacts, setContacts] = useState<any[]>([]);
    const [contactSearch, setContactSearch] = useState('');
    const [showContactDropdown, setShowContactDropdown] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);

    // Derived Selection State
    const allFilteredSelected = emails.length > 0 && emails.every(e => selectedEmailIds.has(e.id));
    const someFilteredSelected = emails.some(e => selectedEmailIds.has(e.id));

    useEffect(() => {
        fetchEmails();
    }, [selectedAgentId]);

    // Fetch contacts when compose modal opens
    useEffect(() => {
        if (showComposeModal && contacts.length === 0) {
            contactsAPI.list().then(res => setContacts(res.data)).catch(console.error);
        }
    }, [showComposeModal]);

    // Handle initial compose from Contacts or a ticket ("email this customer").
    useEffect(() => {
        if (initialComposeEmail) {
            setShowComposeModal(true);
            setComposeData(prev => ({
                ...prev,
                toAddresses: [initialComposeEmail]
            }));
            // Remembered, not applied here: `onComposeHandled` clears the query
            // params straight away, and `agents` may still be loading, so the
            // effect below applies it once that agent is actually in the list.
            setPendingComposeAgentId(initialComposeAgentId || '');
            onComposeHandled?.();
        }
    }, [initialComposeEmail, initialComposeAgentId]);

    // Preselect the ticket's agent once the list has it — and never overwrite a
    // choice the user has already made in the open modal.
    useEffect(() => {
        if (!pendingComposeAgentId) return;
        if (!agents.some(a => a.id === pendingComposeAgentId)) return;
        setComposeData(prev => (prev.agentId ? prev : { ...prev, agentId: pendingComposeAgentId }));
        setPendingComposeAgentId('');
    }, [pendingComposeAgentId, agents]);

    // Handle click outside for dropdown
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setShowContactDropdown(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Staged files belong to the thread they were picked in — switching
    // threads must not carry them over to a different customer.
    useEffect(() => {
        setReplyAttachments([]);
    }, [selectedEmail?.id]);

    const fetchEmails = async () => {
        setLoadingEmails(true);
        try {
            const res = await emailsAPI.list(selectedAgentId || undefined);
            setEmails(res.data);
            setSelectedEmailIds(new Set()); // Reset selection on fetch

            if (selectedEmail) {
                const updated = res.data.find(e => e.id === selectedEmail.id);
                if (updated) setSelectedEmail(updated);
            }

            // Deep-link: auto-open a specific email thread after load
            if (initialEmailId) {
                const match = res.data.find(e => e.id === initialEmailId);
                if (match) {
                    setSelectedEmail(match);
                    setMobilePanel('chat');
                    if (!match.is_read) handleMarkEmailRead(match.id);
                }
                onInitialEmailHandled?.();
            }
        } catch (err) {
            showToast('Failed to load emails', 'error');
        } finally {
            setLoadingEmails(false);
        }
    };

    const toggleSelectAll = () => {
        if (allFilteredSelected) {
            setSelectedEmailIds(new Set());
        } else {
            setSelectedEmailIds(new Set(emails.map(e => e.id)));
        }
    };

    const toggleSelectEmail = (emailId: string, e: React.MouseEvent) => {
        e.stopPropagation();
        setSelectedEmailIds(prev => {
            const next = new Set(prev);
            next.has(emailId) ? next.delete(emailId) : next.add(emailId);
            return next;
        });
    };

    const exportEmails = (format: 'csv' | 'json', onlySelected = false) => {
        const source = onlySelected
            ? emails.filter(e => selectedEmailIds.has(e.id))
            : emails;

        if (source.length === 0) return;

        let content = '';
        let mimeType = '';
        let ext = '';

        if (format === 'json') {
            content = JSON.stringify(source, null, 2);
            mimeType = 'application/json';
            ext = 'json';
        } else if (format === 'csv') {
            const esc = (v: any) => `"${(v ?? '').toString().replace(/"/g, '""')}"`;
            const headers = [
                'Thread ID', 'Customer Name', 'Customer Email', 'Subject', 'Status',
                'Total Messages', 'Is Read', 'Created At', 'Updated At',
                'Agent ID', 'Assigned To', 'Source'
            ];
            const rows = source.map(e => [
                e.id,
                e.customer_name ?? '',
                e.customer_email ?? '',
                e.subject ?? '',
                e.status,
                e.messages?.length ?? 0,
                e.is_read ? 'Yes' : 'No',
                e.created_at,
                e.updated_at,
                e.agent_id,
                e.assigned_to_name ?? 'Unassigned',
                '', // No source metadata for emails in original implementation
            ]);
            content = [headers.map(esc).join(','), ...rows.map(r => r.map(esc).join(','))].join('\n');
            mimeType = 'text/csv';
            ext = 'csv';
        }

        const blob = new Blob([content], { type: mimeType });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `emails_export_${new Date().toISOString().split('T')[0]}.${ext}`;
        a.click();
        URL.revokeObjectURL(url);
    };

    const handleSelectEmail = (emailThread: EmailThread) => {
        setSelectedEmail(emailThread);
        setMobilePanel('chat');
        if (!emailThread.is_read) {
            handleMarkEmailRead(emailThread.id);
        }
    };

    const handleMarkEmailRead = async (threadId: string) => {
        try {
            await emailsAPI.markAsRead(threadId);
            setEmails(prev => prev.map(e => e.id === threadId ? { ...e, is_read: true } : e));
            if (selectedEmail?.id === threadId) {
                setSelectedEmail({ ...selectedEmail, is_read: true });
            }
        } catch (err) {
            showToast('Failed to mark email as read', 'error');
        }
    };

    const handleBackToList = () => {
        setSelectedEmail(null);
        setShowDetails(false);
        setMobilePanel('list');
    };

    const formatBytes = (bytes: number) => bytes < 1024 * 1024
        ? `${(bytes / 1024).toFixed(0)} KB`
        : `${(bytes / (1024 * 1024)).toFixed(1)} MB`;

    // Outbound messages now store the same object shape as inbound ones, but
    // OLD outbound messages stored a bare URL string. Render both.
    const normalizeAttachment = (att: any): { s3_url: string; s3_key: string; filename: string; mime_type: string; file_size_bytes: number; type: string } => {
        if (typeof att === 'string') {
            let filename = att;
            try {
                filename = decodeURIComponent(new URL(att).pathname.split('/').pop() || att);
            } catch {
                filename = decodeURIComponent(att.split('?')[0].split('/').pop() || att);
            }
            return {
                s3_url: att,
                s3_key: '',
                filename: filename || 'attachment',
                mime_type: '',
                file_size_bytes: 0,
                type: /\.(png|jpe?g|gif|webp)$/i.test(filename) ? 'image' : 'document',
            };
        }
        return {
            s3_url: att?.s3_url || '',
            s3_key: att?.s3_key || '',
            filename: att?.filename || 'attachment',
            mime_type: att?.mime_type || '',
            file_size_bytes: att?.file_size_bytes || 0,
            type: att?.type || 'document',
        };
    };

    const handleAddAttachmentToTicket = async (att: { s3_url: string; s3_key: string; filename: string; mime_type: string; file_size_bytes: number; type: string }) => {
        if (!selectedEmail?.ticket_id) {
            showToast('Link this email to a ticket first.', 'error');
            return;
        }
        try {
            await emailsAPI.addAttachmentsToTicket(selectedEmail.id, selectedEmail.ticket_id, [{
                s3_url: att.s3_url,
                s3_key: att.s3_key,
                filename: att.filename,
                mime_type: att.mime_type,
                file_size_bytes: att.file_size_bytes,
                type: att.type,
            }]);
            showToast('Attachment added to ticket.', 'success');
        } catch (err: any) {
            showToast(err.response?.data?.detail || 'Failed to add attachment to ticket.', 'error');
        }
    };

    const handleToggleDetails = () => {
        const next = !showDetails;
        setShowDetails(next);
        if (next) setMobilePanel('details');
        else setMobilePanel('chat');
    };

    const navigate = useNavigate();

    const handleNavigateToTicket = () => {
        if (!selectedEmail?.ticket_id) return;
        navigate(`/tickets?ticket=${selectedEmail.ticket_id}`);
    };

    const handleAssignEmail = async (userId: string) => {
        if (!selectedEmail) return;
        setAssigning(true);
        try {
            await emailsAPI.assign(selectedEmail.id, userId);
            showToast('Email assigned successfully', 'success');
            const member = teamMembers.find(m => m.id === userId);
            setSelectedEmail({ ...selectedEmail, assigned_to: userId, assigned_to_name: member?.full_name });
            fetchEmails();
        } catch (err) {
            showToast('Failed to assign email', 'error');
        } finally {
            setAssigning(false);
        }
    };

    // Close/cancel must not leave files staged for the next email.
    const closeComposeModal = () => {
        setShowComposeModal(false);
        setComposeData({ agentId: '', toAddresses: [], subject: '', textBody: '', htmlBody: '' });
        setComposeAttachments([]);
        setContactSearch('');
        setShowContactDropdown(false);
    };

    const handleSendNewEmail = async () => {
        if (!composeData.agentId || composeData.toAddresses.length === 0 || !composeData.subject || !composeData.textBody) {
            showToast('Please fill all required fields', 'error');
            return;
        }
        setSendingNew(true);
        try {
            await emailsAPI.sendNew({
                agent_id: composeData.agentId,
                to_address: composeData.toAddresses.join(', '),
                subject: composeData.subject,
                text_body: composeData.textBody,
                html_body: composeData.htmlBody || composeData.textBody.replace(/\n/g, '<br>'),
                attachment_refs: composeAttachments,
            });
            closeComposeModal();
            showToast('Email sent successfully', 'success');
            fetchEmails();
        } catch (err) {
            showToast('Failed to send email', 'error');
        } finally {
            setSendingNew(false);
        }
    };

    const handleEmailReply = async () => {
        if (!selectedEmail || !replyText.trim()) return;
        setSendingReply(true);
        try {
            await emailsAPI.reply(selectedEmail.id, {
                text_body: replyText,
                html_body: replyHtml,
                attachment_refs: replyAttachments,
            });
            setReplyText('');
            setReplyHtml('');
            setReplyAttachments([]);
            showToast('Reply sent successfully via AWS SES', 'success');
            fetchEmails();
        } catch (err) {
            showToast('Failed to send email reply', 'error');
        } finally {
            setSendingReply(false);
        }
    };

    const handleAddEmailComment = async () => {
        if (!selectedEmail || !commentText.trim()) return;
        setAddingComment(true);
        try {
            await emailsAPI.addComment(selectedEmail.id, commentText);
            setCommentText('');
            await fetchEmails();
            showToast('Comment added', 'success');
        } catch {
            showToast('Failed to add comment', 'error');
        } finally {
            setAddingComment(false);
        }
    };

    const handleGenerateReply = async () => {
        if (!selectedEmail) return;
        setGeneratingReply(true);
        try {
            const res = await emailsAPI.generateReply(selectedEmail.id);
            const draft = res.data.reply;
            setReplyText(draft);
            setReplyHtml(draft.replace(/\n/g, '<br>'));
            showToast('AI reply draft generated — review and edit before sending', 'success');
        } catch {
            showToast('Failed to generate AI reply. Please try again.', 'error');
        } finally {
            setGeneratingReply(false);
        }
    };

    return (
        <div className="flex-1 flex overflow-hidden md:p-4 lg:p-6 md:gap-0">
            <div className="flex-1 flex overflow-hidden md:rounded-2xl md:shadow-sm md:border md:border-gray-200 md:dark:border-gray-800 bg-white dark:bg-gray-900">

                {/* ── Email List Panel ── */}
                <div className={`
                    flex flex-col flex-shrink-0
                    border-r border-gray-200 dark:border-gray-800
                    bg-white dark:bg-gray-900
                    transition-all duration-300
                    ${mobilePanel === 'list' ? 'flex' : 'hidden'}
                    md:flex
                    ${selectedEmail ? 'md:w-64 lg:w-72' : 'w-full md:w-full'}
                `}>
                    <div className="p-3 md:p-4 border-b border-gray-200 dark:border-gray-800 bg-gray-50/50 dark:bg-gray-900/50 flex-shrink-0 space-y-2">
                        <div className="flex items-center justify-between gap-2">
                            <div className="flex items-center gap-2 min-w-0">
                                <button
                                    onClick={toggleSelectAll}
                                    className="flex-shrink-0 p-0.5 text-gray-400 hover:text-primary transition-colors"
                                    title={allFilteredSelected ? 'Deselect all' : 'Select all'}
                                >
                                    {allFilteredSelected
                                        ? <CheckSquare className="w-4 h-4 text-primary" />
                                        : someFilteredSelected
                                            ? <CheckSquare className="w-4 h-4 text-primary/50" />
                                            : <Square className="w-4 h-4" />}
                                </button>
                                <h2 className="font-semibold text-gray-900 dark:text-white text-sm truncate">Email Threads</h2>
                            </div>
                            <div className="flex items-center gap-1 flex-shrink-0">
                                <button onClick={() => exportEmails('csv')} className="p-1.5 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors" title="Export all CSV">
                                    <Download className="w-4 h-4" />
                                </button>
                                <button onClick={() => exportEmails('json')} className="p-1.5 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors font-medium text-xs" title="Export all JSON">
                                    JSON
                                </button>
                                <button
                                    onClick={() => setShowComposeModal(true)}
                                    className="p-1.5 text-primary hover:bg-primary/10 rounded-md transition-colors ml-1"
                                    title="Compose New Email"
                                >
                                    <Send className="w-4 h-4" />
                                </button>
                            </div>
                        </div>

                        <select
                            className="w-full text-xs bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg px-2 py-1.5 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary/20 focus:border-primary"
                            value={selectedAgentId}
                            onChange={(e) => {
                                setSelectedAgentId(e.target.value);
                                setSelectedEmailIds(new Set());
                            }}
                        >
                            <option value="">All Agents</option>
                            {agents.map(a => (
                                <option key={a.id} value={a.id}>{a.config.agent_name}</option>
                            ))}
                        </select>

                        {someFilteredSelected && (
                            <div className="flex items-center justify-between bg-primary/5 dark:bg-primary/10 rounded-lg px-2 py-1.5 border border-primary/20 mt-2">
                                <span className="text-xs font-medium text-primary">
                                    {selectedEmailIds.size} selected
                                </span>
                                <div className="flex items-center gap-1">
                                    <button
                                        onClick={() => exportEmails('csv', true)}
                                        className="text-xs px-2 py-1 rounded-md bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:border-primary hover:text-primary transition-colors flex items-center gap-1"
                                    >
                                        <Download className="w-3 h-3" /> CSV
                                    </button>
                                    <button
                                        onClick={() => exportEmails('json', true)}
                                        className="text-xs px-2 py-1 rounded-md bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:border-primary hover:text-primary transition-colors flex items-center gap-1"
                                    >
                                        <Download className="w-3 h-3" /> JSON
                                    </button>
                                    <button
                                        onClick={() => setSelectedEmailIds(new Set())}
                                        className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors ml-1"
                                        title="Clear selection"
                                    >
                                        <X className="w-3.5 h-3.5" />
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="flex-1 overflow-y-auto">
                        {loadingEmails ? (
                            <div className="flex justify-center p-8">
                                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary" />
                            </div>
                        ) : emails.length === 0 ? (
                            <div className="p-8 text-center text-gray-500 dark:text-gray-400 text-sm">
                                No emails found.
                            </div>
                        ) : (
                            <div className="divide-y divide-gray-100 dark:divide-gray-800">
                                {emails.map(emailThread => {
                                    const isChecked = selectedEmailIds.has(emailThread.id);
                                    return (
                                        <div
                                            key={emailThread.id}
                                            onClick={() => handleSelectEmail(emailThread)}
                                            className={`group relative p-4 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors ${selectedEmail?.id === emailThread.id ? 'bg-primary/5 dark:bg-primary/10 border-l-2 border-primary' : ''
                                                } ${!emailThread.is_read || isChecked ? 'bg-primary/5 dark:bg-primary/10' : ''}`}
                                        >
                                            <div className="flex items-start gap-2">
                                                <button
                                                    onClick={(e) => toggleSelectEmail(emailThread.id, e)}
                                                    className={`flex-shrink-0 mt-0.5 transition-opacity ${isChecked || someFilteredSelected ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
                                                        }`}
                                                    title={isChecked ? 'Deselect' : 'Select'}
                                                >
                                                    {isChecked
                                                        ? <CheckSquare className="w-4 h-4 text-primary" />
                                                        : <Square className="w-4 h-4 text-gray-400" />}
                                                </button>
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex justify-between items-start mb-1">
                                                        <span className={`font-medium text-sm text-gray-900 dark:text-white truncate pr-2 ${!emailThread.is_read ? 'font-bold' : ''}`}>
                                                            {emailThread.customer_name || emailThread.customer_email || 'Unknown User'}
                                                        </span>
                                                        <span className="text-xs text-gray-400 whitespace-nowrap">
                                                            {formatServerDate(emailThread.updated_at)}
                                                        </span>
                                                    </div>
                                                    <div className="text-xs text-gray-600 dark:text-gray-300 truncate font-medium">
                                                        {emailThread.subject}
                                                    </div>
                                                    <div className="flex items-center justify-between mt-1.5">
                                                        <span className={`text-xs px-2 py-0.5 rounded-full ${emailThread.status === 'open'
                                                            ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
                                                            : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400'
                                                            }`}>
                                                            {emailThread.status}
                                                        </span>
                                                    </div>
                                                </div>
                                                {!emailThread.is_read && (
                                                    <div className="w-2 h-2 rounded-full bg-primary mt-1.5 flex-shrink-0" />
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                </div>

                {/* ── Email Detail Panel ── */}
                {selectedEmail && (
                    <div className={`
                        flex-1 flex flex-col min-w-0
                        border-r border-gray-200 dark:border-gray-800
                        bg-white dark:bg-gray-900
                        ${mobilePanel === 'chat' ? 'flex' : 'hidden'}
                        md:flex
                    `}>
                        <div className="p-3 md:p-4 border-b border-gray-200 dark:border-gray-800 flex items-center justify-between bg-gray-50/50 dark:bg-gray-900/50 shadow-sm z-10 flex-shrink-0">
                            <div className="flex items-center gap-2 md:gap-3 min-w-0">
                                <button
                                    onClick={handleBackToList}
                                    className="md:hidden p-1.5 -ml-1 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 flex-shrink-0"
                                >
                                    <X className="w-4 h-4 rotate-180" />
                                </button>
                                <div className="w-8 h-8 md:w-10 md:h-10 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center text-blue-600 dark:text-blue-400 font-bold flex-shrink-0 text-sm">
                                    {(selectedEmail.customer_name?.charAt(0) || selectedEmail.customer_email.charAt(0)).toUpperCase()}
                                </div>
                                <div className="min-w-0">
                                    <h3 className="font-semibold text-gray-900 dark:text-white text-sm md:text-base truncate">
                                        {selectedEmail.subject}
                                    </h3>
                                    <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                                        {selectedEmail.customer_email}
                                    </p>
                                </div>
                            </div>
                            <div className="flex items-center gap-1 md:gap-2 flex-shrink-0">
                                {hasAssignPermission && (
                                    <select
                                        className="hidden sm:block text-xs md:text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white border border-gray-200 dark:border-gray-700 rounded-lg px-2 md:px-3 py-1 md:py-1.5 focus:ring-2 focus:ring-primary/20 focus:border-primary disabled:opacity-50 max-w-[120px] md:max-w-none"
                                        value={selectedEmail.assigned_to || ""}
                                        onChange={(e) => handleAssignEmail(e.target.value)}
                                        disabled={assigning}
                                    >
                                        <option value="" disabled>Assign to...</option>
                                        {teamMembers.map(m => (
                                            <option key={m.id} value={m.id}>{m.full_name}</option>
                                        ))}
                                    </select>
                                )}
                                <button
                                    onClick={handleToggleDetails}
                                    className={`p-1.5 md:p-2 rounded-lg transition-colors ${showDetails ? 'bg-primary/10 text-primary' : 'text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-white'}`}
                                    title="Email Details"
                                >
                                    <Info className="w-4 h-4 md:w-5 md:h-5" />
                                </button>
                                <button
                                    onClick={handleBackToList}
                                    className="hidden md:flex p-1.5 md:p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800"
                                    title="Close"
                                >
                                    <X className="w-4 h-4 md:w-5 md:h-5" />
                                </button>
                            </div>
                        </div>

                        <div className="flex-1 overflow-y-auto p-4 md:p-8 space-y-8 bg-gray-50 dark:bg-[#0a0a0a]">
                            {selectedEmail.messages.map((msg, idx) => (
                                <div key={idx} className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden">
                                    <div className="p-4 border-b border-gray-100 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-900/50">
                                        <div className="flex justify-between items-start gap-4">
                                            <div className="min-w-0 flex-1">
                                                <div className="font-medium text-sm text-gray-900 dark:text-white truncate">
                                                    {msg.from_name ? `${msg.from_name} <${msg.from_address}>` : msg.from_address}
                                                </div>
                                                <div className="text-xs text-gray-500 dark:text-gray-400 truncate mt-0.5">
                                                    to: {msg.to_address}
                                                </div>
                                            </div>
                                            <div className="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">
                                                {formatServerDateTime(msg.received_at)}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="p-4 md:p-6 text-sm text-gray-800 dark:text-gray-200">
                                        {msg.html_body ? (
                                            <div dangerouslySetInnerHTML={{ __html: msg.html_body }} className="mail-content prose dark:prose-invert max-w-none text-sm" />
                                        ) : (
                                            <div className="whitespace-pre-wrap font-sans">{msg.text_body}</div>
                                        )}
                                    </div>
                                    {msg.attachments && msg.attachments.length > 0 && (
                                        <div className="px-4 pb-4 pt-2 border-t border-gray-100 dark:border-gray-700">
                                            <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2">Attachments ({msg.attachments.length})</p>
                                            <div className="flex flex-wrap gap-3">
                                                {msg.attachments.map((raw: any, i: number) => {
                                                    const att = normalizeAttachment(raw);
                                                    return (
                                                    <div key={i} className="flex flex-col items-start gap-1">
                                                        {att.type === 'image' ? (
                                                            <div>
                                                                <a href={att.s3_url} target="_blank" rel="noopener noreferrer">
                                                                    <img
                                                                        src={att.s3_url}
                                                                        alt={att.filename}
                                                                        className="max-h-32 rounded cursor-pointer border border-gray-200 dark:border-gray-700"
                                                                    />
                                                                </a>
                                                                <p className="text-[10px] text-gray-500 dark:text-gray-400 mt-1 truncate max-w-[10rem]">{att.filename}</p>
                                                            </div>
                                                        ) : (
                                                            <a
                                                                href={att.s3_url}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                className="flex items-center gap-1.5 text-xs text-primary hover:underline bg-gray-50 dark:bg-gray-800 px-2 py-1.5 rounded-lg border border-gray-200 dark:border-gray-700"
                                                            >
                                                                <FileText className="w-3.5 h-3.5" />
                                                                <span className="truncate max-w-[10rem]">{att.filename}</span>
                                                                {att.file_size_bytes > 0 && (
                                                                    <span className="text-gray-400 dark:text-gray-500 ml-1">{formatBytes(att.file_size_bytes)}</span>
                                                                )}
                                                            </a>
                                                        )}
                                                        <button
                                                            onClick={() => handleAddAttachmentToTicket(att)}
                                                            className="text-xs text-gray-500 hover:text-primary transition-colors bg-gray-50 dark:bg-gray-800 px-2 py-1.5 rounded-lg border border-gray-200 dark:border-gray-700 flex items-center gap-1"
                                                            title="Add to linked ticket"
                                                        >
                                                            <Paperclip className="w-3 h-3" />
                                                            Add to ticket
                                                        </button>
                                                    </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>

                        <div className="p-4 border-t border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 flex-shrink-0 flex flex-col gap-3">
                            <RichTextEditor
                                content={replyHtml}
                                onChange={(html, text) => {
                                    setReplyHtml(html);
                                    setReplyText(text);
                                }}
                                placeholder="Write a reply..."
                            />
                            <AttachmentPicker
                                attachments={replyAttachments}
                                onChange={setReplyAttachments}
                                onUploadingChange={setReplyUploading}
                                disabled={sendingReply}
                            />
                            <div className="flex items-center justify-between gap-2 flex-wrap">
                                <div className="flex flex-col gap-1.5">
                                    <p className="text-xs text-gray-500 dark:text-gray-400">
                                        Attachments are logged securely.
                                    </p>
                                    {currentAgent && (
                                        <div className="flex flex-col gap-2">
                                            <label className={`flex items-center gap-2 cursor-pointer w-max ${updatingPublished ? 'opacity-50 pointer-events-none' : ''}`}>
                                                <span className="text-xs font-medium text-gray-600 dark:text-gray-300 flex items-center gap-1">
                                                    Status: {publishedEnabled ? <span className="text-blue-600 dark:text-blue-400">Live</span> : <span className="text-gray-500">Draft</span>}
                                                </span>
                                                <button
                                                    type="button"
                                                    onClick={handleTogglePublished}
                                                    className={`relative inline-flex h-4 w-7 items-center rounded-full transition-colors flex-shrink-0 ${publishedEnabled ? 'bg-blue-600' : 'bg-gray-300 dark:bg-gray-600'}`}
                                                >
                                                    <span className={`inline-block h-2.5 w-2.5 transform rounded-full bg-white transition-transform ${publishedEnabled ? 'translate-x-3.5' : 'translate-x-0.5'}`} />
                                                </button>
                                            </label>
                                            <label className={`flex items-center gap-2 cursor-pointer w-max ${updatingAutoReply ? 'opacity-50 pointer-events-none' : ''}`}>
                                                <span className="text-xs font-medium text-gray-600 dark:text-gray-300 flex items-center gap-1">
                                                    <Bot className="w-3.5 h-3.5" /> Auto-Respond
                                                </span>
                                                <button
                                                    type="button"
                                                    onClick={handleToggleAutoReply}
                                                    className={`relative inline-flex h-4 w-7 items-center rounded-full transition-colors flex-shrink-0 ${autoReplyEnabled ? 'bg-primary' : 'bg-gray-300 dark:bg-gray-600'}`}
                                                >
                                                    <span className={`inline-block h-2.5 w-2.5 transform rounded-full bg-white transition-transform ${autoReplyEnabled ? 'translate-x-3.5' : 'translate-x-0.5'}`} />
                                                </button>
                                            </label>
                                        </div>
                                    )}
                                </div>
                                <div className="flex items-center gap-2">
                                    <button
                                        onClick={handleGenerateReply}
                                        disabled={generatingReply || sendingReply}
                                        className="flex items-center gap-1.5 px-3 py-2 bg-purple-50 hover:bg-purple-100 dark:bg-purple-900/20 dark:hover:bg-purple-900/40 text-purple-700 dark:text-purple-300 border border-purple-200 dark:border-purple-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
                                        title="Generate a draft reply using AI — you can edit it before sending"
                                    >
                                        {generatingReply
                                            ? <div className="w-4 h-4 border-2 border-purple-400/30 border-t-purple-500 rounded-full animate-spin" />
                                            : <Sparkles className="w-4 h-4" />}
                                        <span>{generatingReply ? 'Generating...' : 'Generate AI Reply'}</span>
                                    </button>
                                    <button
                                        onClick={handleEmailReply}
                                        disabled={!replyText.trim() || sendingReply || replyUploading}
                                        className="flex items-center gap-2 px-4 py-2 bg-primary hover:bg-primary/90 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium text-sm shadow-sm"
                                    >
                                        {sendingReply ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <Send className="w-4 h-4" />}
                                        <span>Send Reply</span>
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* ── Email Details Panel ── */}
                {selectedEmail && showDetails && (
                    <div className={`
                        flex-shrink-0 overflow-hidden
                        bg-gray-50 dark:bg-gray-900
                        border-l border-gray-200 dark:border-gray-800
                        ${mobilePanel === 'details' ? 'flex flex-col w-full' : 'hidden'}
                        lg:flex lg:flex-col lg:w-72
                        md:flex md:flex-col md:w-64
                        p-4 md:p-5
                    `}>
                        <div className="flex items-center justify-between mb-5 flex-shrink-0">
                            <div className="flex items-center gap-2">
                                <button
                                    onClick={() => setMobilePanel('chat')}
                                    className="md:hidden p-1 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 rounded-lg"
                                    title="Back to chat"
                                >
                                    <X className="w-4 h-4 rotate-180" />
                                </button>
                                <h3 className="font-semibold text-gray-900 dark:text-white">Details</h3>
                            </div>
                            <button onClick={() => { setShowDetails(false); setMobilePanel('chat'); }} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors">
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        <div className="flex-1 overflow-y-auto space-y-6">
                            <div className="flex-shrink-0">
                                <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">Thread Info</p>
                                <div className="bg-white dark:bg-gray-800 rounded-xl p-3 border border-gray-100 dark:border-gray-700 shadow-sm space-y-3">
                                    <div>
                                        <p className="text-xs text-gray-500 dark:text-gray-400">Status</p>
                                        <p className="text-sm font-medium text-gray-900 dark:text-white capitalize">{selectedEmail.status}</p>
                                    </div>
                                    <div>
                                        <p className="text-xs text-gray-500 dark:text-gray-400">Received</p>
                                        <p className="text-sm font-medium text-gray-900 dark:text-white">{formatServerDateTime(selectedEmail.updated_at)}</p>
                                    </div>
                                    <div>
                                        <p className="text-xs text-gray-500 dark:text-gray-400">Messages</p>
                                        <p className="text-sm font-medium text-gray-900 dark:text-white">{selectedEmail.messages.length}</p>
                                    </div>
                                    <div>
                                        <p className="text-xs text-gray-500 dark:text-gray-400">System Info</p>
                                        <p className="text-xs font-mono text-gray-900 dark:text-white mt-1">ID: {selectedEmail.id}</p>
                                    </div>
                                </div>
                            </div>

                            {/* CSAT Rating */}
                            {(selectedEmail as any).csat_score != null && (
                                <div className="flex-shrink-0">
                                    <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">Customer Satisfaction</p>
                                    <div className="bg-white dark:bg-gray-800 rounded-xl p-3 border border-gray-100 dark:border-gray-700 shadow-sm space-y-2">
                                        <div className="flex items-center gap-1">
                                            {[1, 2, 3, 4, 5].map((star) => (
                                                <span key={star} className={`text-lg ${star <= (selectedEmail as any).csat_score ? 'text-amber-400' : 'text-gray-200 dark:text-gray-600'}`}>
                                                    ★
                                                </span>
                                            ))}
                                            <span className="ml-1 text-sm font-semibold text-gray-900 dark:text-white">{(selectedEmail as any).csat_score}/5</span>
                                        </div>
                                        {(selectedEmail as any).csat_feedback && (
                                            <p className="text-xs text-gray-600 dark:text-gray-400 italic">"{(selectedEmail as any).csat_feedback}"</p>
                                        )}
                                    </div>
                                </div>
                            )}

                            <div className="flex-shrink-0">
                                <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">Assignment</p>
                                <div className="bg-white dark:bg-gray-800 rounded-xl p-3 border border-gray-100 dark:border-gray-700 shadow-sm">
                                    {selectedEmail.assigned_to_name ? (
                                        <div className="flex items-center gap-2">
                                            <div className="w-8 h-8 rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 flex items-center justify-center font-bold text-xs">
                                                {selectedEmail.assigned_to_name.charAt(0)}
                                            </div>
                                            <span className="text-sm font-medium text-gray-900 dark:text-white">{selectedEmail.assigned_to_name}</span>
                                        </div>
                                    ) : (
                                        <p className="text-sm text-gray-500 dark:text-gray-400 italic">Unassigned</p>
                                    )}
                                </div>
                            </div>

                            {hasAssignPermission && (
                                <div className="flex-shrink-0">
                                    <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">Reassign</p>
                                    <select
                                        className="sm:hidden w-full text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary/20 focus:border-primary disabled:opacity-50"
                                        value={selectedEmail.assigned_to || ""}
                                        onChange={(e) => handleAssignEmail(e.target.value)}
                                        disabled={assigning}
                                    >
                                        <option value="" disabled>Assign to...</option>
                                        {teamMembers.map(m => (
                                            <option key={m.id} value={m.id}>{m.full_name}</option>
                                        ))}
                                    </select>
                                </div>
                            )}

                            {(selectedEmail as any).ticket_id && (
                                <div>
                                    <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">Linked Ticket</p>
                                    <div className="bg-white dark:bg-gray-800 rounded-xl p-3 border border-gray-100 dark:border-gray-700 shadow-sm">
                                        <button
                                            onClick={handleNavigateToTicket}
                                            className="flex items-center space-x-1.5 text-sm font-medium text-primary hover:text-primary/80 transition-colors w-full"
                                        >
                                            <ExternalLink className="w-4 h-4" />
                                            <span>View Ticket #{(selectedEmail as any).ticket_id.split('-')[1] || (selectedEmail as any).ticket_id}</span>
                                        </button>
                                    </div>
                                </div>
                            )}

                            <div className="border-t border-gray-200 dark:border-gray-800 pt-5 mt-5">
                                <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3 flex-shrink-0">Internal Comments</p>

                                <div className="space-y-3 mb-4 pr-1">
                                    {(!selectedEmail.comments || selectedEmail.comments.length === 0) ? (
                                        <p className="text-sm text-gray-500 dark:text-gray-400 italic">No comments yet.</p>
                                    ) : (
                                        selectedEmail.comments.map(c => (
                                            <div key={c.comment_id} className="bg-white dark:bg-gray-800 rounded-xl p-3 border border-gray-100 dark:border-gray-700 shadow-sm">
                                                <div className="flex justify-between items-start mb-1">
                                                    <span className="font-medium text-xs text-gray-900 dark:text-white">{c.author_name}</span>
                                                    <span className="text-[10px] text-gray-400">{formatServerDateTime(c.created_at, [], { dateStyle: 'short', timeStyle: 'short' })}</span>
                                                </div>
                                                <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">{c.text}</p>
                                            </div>
                                        ))
                                    )}
                                </div>

                                <div className="flex-shrink-0 relative">
                                    <textarea
                                        value={commentText}
                                        onChange={e => setCommentText(e.target.value)}
                                        placeholder="Add an internal note..."
                                        className="w-full text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-gray-500 border border-gray-200 dark:border-gray-700 rounded-xl px-3 py-2.5 pb-10 focus:ring-2 focus:ring-primary/20 focus:border-primary resize-none h-24 shadow-sm"
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter' && !e.shiftKey) {
                                                e.preventDefault();
                                                handleAddEmailComment();
                                            }
                                        }}
                                    />
                                    <button
                                        onClick={handleAddEmailComment}
                                        disabled={!commentText.trim() || addingComment}
                                        className="absolute bottom-2 right-2 p-1.5 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 rounded-lg transition-colors disabled:opacity-50"
                                        title="Add comment"
                                    >
                                        {addingComment ? <div className="w-4 h-4 border-2 border-gray-500 border-t-transparent rounded-full animate-spin" /> : <Send className="w-4 h-4" />}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Compose Email Modal */}
            {showComposeModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
                    <div className="bg-white dark:bg-gray-900 rounded-xl shadow-xl w-full max-w-2xl overflow-hidden flex flex-col max-h-[90vh]">
                        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-800">
                            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Compose New Email</h3>
                            <button onClick={closeComposeModal} className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200">
                                <X className="w-5 h-5" />
                            </button>
                        </div>
                        <div className="p-4 overflow-y-auto flex-1 space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">From Agent</label>
                                <select
                                    className="w-full text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary/20 outline-none"
                                    value={composeData.agentId}
                                    onChange={(e) => setComposeData({ ...composeData, agentId: e.target.value })}
                                >
                                    <option value="" disabled>Select an Agent</option>
                                    {agents.map(a => (
                                        <option key={a.id} value={a.id}>{a.config.agent_name}</option>
                                    ))}
                                </select>
                                {composeAgent && (
                                    <div className="mt-2 text-right flex items-center justify-end gap-5">
                                        <label className={`inline-flex items-center gap-2 cursor-pointer ${updatingPublished ? 'opacity-50 pointer-events-none' : ''}`}>
                                            <span className="text-xs font-medium text-gray-600 dark:text-gray-300 flex items-center gap-1">
                                                Status: {publishedEnabled ? <span className="text-blue-600 dark:text-blue-400">Live</span> : <span className="text-gray-500">Draft</span>}
                                            </span>
                                            <button
                                                type="button"
                                                onClick={handleTogglePublished}
                                                className={`relative inline-flex h-4 w-7 items-center rounded-full transition-colors flex-shrink-0 ${publishedEnabled ? 'bg-blue-600' : 'bg-gray-300 dark:bg-gray-600'}`}
                                            >
                                                <span className={`inline-block h-2.5 w-2.5 transform rounded-full bg-white transition-transform ${publishedEnabled ? 'translate-x-3.5' : 'translate-x-0.5'}`} />
                                            </button>
                                        </label>
                                        <label className={`inline-flex items-center gap-2 cursor-pointer ${updatingAutoReply ? 'opacity-50 pointer-events-none' : ''}`}>
                                            <span className="text-xs font-medium text-gray-600 dark:text-gray-300 flex items-center gap-1">
                                                <Bot className="w-3.5 h-3.5" /> Auto-Respond
                                            </span>
                                            <button
                                                type="button"
                                                onClick={handleToggleAutoReply}
                                                className={`relative inline-flex h-4 w-7 items-center rounded-full transition-colors flex-shrink-0 ${autoReplyEnabled ? 'bg-primary' : 'bg-gray-300 dark:bg-gray-600'}`}
                                            >
                                                <span className={`inline-block h-2.5 w-2.5 transform rounded-full bg-white transition-transform ${autoReplyEnabled ? 'translate-x-3.5' : 'translate-x-0.5'}`} />
                                            </button>
                                        </label>
                                    </div>
                                )}
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">To</label>
                                <div ref={dropdownRef} className="flex flex-wrap gap-2 p-2 w-full text-sm bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg focus-within:ring-2 focus-within:ring-primary/20 relative items-center">
                                    {composeData.toAddresses.map((email: string) => (
                                        <span key={email} className="flex items-center gap-1 bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200 px-2 py-1 rounded-md text-xs">
                                            {email}
                                            <button
                                                onClick={() => setComposeData(prev => ({ ...prev, toAddresses: prev.toAddresses.filter(e => e !== email) }))}
                                                className="hover:text-red-500"
                                            >
                                                <X className="w-3 h-3" />
                                            </button>
                                        </span>
                                    ))}
                                    <input
                                        type="text"
                                        className="flex-1 bg-transparent outline-none min-w-[120px] text-sm py-0.5"
                                        placeholder="Search contacts or type email..."
                                        value={contactSearch}
                                        onChange={(e) => {
                                            setContactSearch(e.target.value);
                                            setShowContactDropdown(true);
                                        }}
                                        onFocus={() => setShowContactDropdown(true)}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter' && contactSearch.trim()) {
                                                e.preventDefault();
                                                const val = contactSearch.trim();
                                                if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val)) {
                                                    if (!composeData.toAddresses.includes(val)) {
                                                        setComposeData(prev => ({ ...prev, toAddresses: [...prev.toAddresses, val] }));
                                                    }
                                                    setContactSearch('');
                                                    setShowContactDropdown(false);
                                                } else {
                                                    showToast('Please enter a valid email address', 'error');
                                                }
                                            } else if (e.key === 'Backspace' && !contactSearch && composeData.toAddresses.length > 0) {
                                                setComposeData(prev => ({ ...prev, toAddresses: prev.toAddresses.slice(0, -1) }));
                                            }
                                        }}
                                    />
                                    {showContactDropdown && (
                                        <div className="absolute top-full left-0 right-0 mt-1 max-h-48 overflow-y-auto bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-50">
                                            {contacts.filter(c =>
                                                (c.name.toLowerCase().includes(contactSearch.toLowerCase()) ||
                                                    c.email.toLowerCase().includes(contactSearch.toLowerCase())) &&
                                                !composeData.toAddresses.includes(c.email)
                                            ).map(c => (
                                                <div
                                                    key={c.id}
                                                    className="px-3 py-2 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer flex justify-between items-center text-sm"
                                                    onClick={() => {
                                                        setComposeData(prev => ({ ...prev, toAddresses: [...prev.toAddresses, c.email] }));
                                                        setContactSearch('');
                                                        setShowContactDropdown(false);
                                                    }}
                                                >
                                                    <span className="font-medium text-gray-900 dark:text-white">{c.name}</span>
                                                    <span className="text-gray-500 dark:text-gray-400">{c.email}</span>
                                                </div>
                                            ))}
                                            {contactSearch.trim() && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contactSearch.trim()) && !contacts.some(c => c.email === contactSearch.trim()) && (
                                                <div
                                                    className="px-3 py-2 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer flex justify-between items-center text-sm border-t border-gray-100 dark:border-gray-700 text-primary"
                                                    onClick={() => {
                                                        const val = contactSearch.trim();
                                                        if (!composeData.toAddresses.includes(val)) {
                                                            setComposeData(prev => ({ ...prev, toAddresses: [...prev.toAddresses, val] }));
                                                        }
                                                        setContactSearch('');
                                                        setShowContactDropdown(false);
                                                    }}
                                                >
                                                    Use "{contactSearch.trim()}"
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Subject</label>
                                <input
                                    type="text"
                                    className="w-full text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-gray-500 border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary/20 outline-none"
                                    placeholder="Subject"
                                    value={composeData.subject}
                                    onChange={(e) => setComposeData({ ...composeData, subject: e.target.value })}
                                />
                            </div>
                            <div className="min-h-[200px] border border-gray-200 dark:border-gray-800 rounded-lg overflow-hidden flex flex-col">
                                <RichTextEditor
                                    content={composeData.htmlBody}
                                    onChange={(html, text) => setComposeData({ ...composeData, htmlBody: html, textBody: text })}
                                    placeholder="Write your email here..."
                                />
                            </div>
                            <AttachmentPicker
                                attachments={composeAttachments}
                                onChange={setComposeAttachments}
                                onUploadingChange={setComposeUploading}
                                disabled={sendingNew}
                            />
                        </div>
                        <div className="p-4 border-t border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-900 flex justify-end gap-3">
                            <button
                                onClick={closeComposeModal}
                                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleSendNewEmail}
                                disabled={sendingNew || composeUploading || !composeData.agentId || composeData.toAddresses.length === 0 || !composeData.subject || !composeData.textBody}
                                className="px-4 py-2 text-sm font-medium bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors disabled:opacity-50 flex items-center"
                            >
                                {sendingNew ? (
                                    <>
                                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                                        Sending...
                                    </>
                                ) : (
                                    <>
                                        <Send className="w-4 h-4 mr-2" />
                                        Send Email
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default EmailsTab;
